import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Plus, BookOpen, Eye, EyeOff, Heart, Search, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Navbar from '@/components/Navbar';
import { toast } from 'sonner';
import logger from '@/utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoadingIndicator = () => (
  <div className="flex justify-center py-8">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const PatternCard = ({ pattern, isMine, onNavigate, onLike, t }) => {
  const getDifficultyLabel = (difficulty) => {
    return t(`patterns.difficulty.${difficulty}`, difficulty);
  };

  return (
    <div
      className="rounded-3xl bg-white border border-stone-100 p-6 hover:shadow-xl transition-all cursor-pointer"
      onClick={() => onNavigate(`/patterns/${pattern.pattern_id}`)}
    >
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-xl font-heading font-semibold mb-1">{pattern.name}</h3>
          <p className="text-sm text-muted-foreground">{t('patterns.by', 'by')} {pattern.creator_name}</p>
        </div>
        <div className="flex items-center space-x-2">
          {isMine ? (
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              pattern.is_public ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-600'
            }`}>
              {pattern.is_public ? <Eye className="h-3 w-3 inline mr-1" /> : <EyeOff className="h-3 w-3 inline mr-1" />}
              {pattern.is_public ? t('patterns.public', 'Public') : t('patterns.private', 'Private')}
            </span>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onLike(pattern.pattern_id);
              }}
              className="h-8 w-8 p-0"
            >
              <Heart className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
        {pattern.description}
      </p>

      <div className="flex items-center justify-between">
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
          pattern.difficulty === 'beginner' ? 'bg-green-100 text-green-700' :
          pattern.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-700' :
          'bg-red-100 text-red-700'
        }`}>
          {getDifficultyLabel(pattern.difficulty)}
        </span>
        <div className="flex items-center space-x-1 text-muted-foreground">
          <Heart className="h-4 w-4" />
          <span className="text-sm">{pattern.likes_count}</span>
        </div>
      </div>

      {pattern.tags && pattern.tags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {pattern.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="px-2 py-1 rounded-full bg-stone-100 text-xs">
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Patterns() {
  const { t } = useTranslation();
  const [myPatterns, setMyPatterns] = useState([]);
  const [publicPatterns, setPublicPatterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMoreMy, setLoadingMoreMy] = useState(false);
  const [loadingMorePublic, setLoadingMorePublic] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [myPage, setMyPage] = useState(1);
  const [publicPage, setPublicPage] = useState(1);
  const [hasMoreMy, setHasMoreMy] = useState(true);
  const [hasMorePublic, setHasMorePublic] = useState(true);
  const [myTotal, setMyTotal] = useState(0);
  const [publicTotal, setPublicTotal] = useState(0);
  const myObserver = useRef();
  const publicObserver = useRef();
  const navigate = useNavigate();

  const lastMyPatternRef = useCallback(node => {
    if (loadingMoreMy) return;
    if (myObserver.current) myObserver.current.disconnect();
    myObserver.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMoreMy) {
        setMyPage(prev => prev + 1);
      }
    });
    if (node) myObserver.current.observe(node);
  }, [loadingMoreMy, hasMoreMy]);

  const lastPublicPatternRef = useCallback(node => {
    if (loadingMorePublic) return;
    if (publicObserver.current) publicObserver.current.disconnect();
    publicObserver.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMorePublic) {
        setPublicPage(prev => prev + 1);
      }
    });
    if (node) publicObserver.current.observe(node);
  }, [loadingMorePublic, hasMorePublic]);

  const loadMyPatterns = useCallback(async () => {
    if (myPage === 1) setLoading(true);
    else setLoadingMoreMy(true);
    
    try {
      const params = new URLSearchParams();
      params.append('page', myPage);
      params.append('page_size', '12');
      
      const response = await axios.get(`${API}/patterns?${params.toString()}`, { withCredentials: true });
      const newItems = response.data.items || response.data;
      
      if (myPage === 1) {
        setMyPatterns(newItems);
      } else {
        setMyPatterns(prev => [...prev, ...newItems]);
      }
      
      setMyTotal(response.data.total || newItems.length);
      setHasMoreMy(myPage < (response.data.total_pages || 1));
    } catch (error) {
      logger.error('Failed to load my patterns:', error);
    } finally {
      setLoading(false);
      setLoadingMoreMy(false);
    }
  }, [myPage]);

  const loadPublicPatterns = useCallback(async () => {
    if (publicPage === 1 && !searchTerm) return;
    if (publicPage > 1) setLoadingMorePublic(true);
    
    try {
      const params = new URLSearchParams();
      params.append('page', publicPage);
      params.append('page_size', '12');
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await axios.get(`${API}/patterns/public?${params.toString()}`);
      const newItems = response.data.items || response.data;
      
      if (publicPage === 1) {
        setPublicPatterns(newItems);
      } else {
        setPublicPatterns(prev => [...prev, ...newItems]);
      }
      
      setPublicTotal(response.data.total || newItems.length);
      setHasMorePublic(publicPage < (response.data.total_pages || 1));
    } catch (error) {
      logger.error('Failed to load public patterns:', error);
    } finally {
      setLoadingMorePublic(false);
    }
  }, [publicPage, searchTerm]);

  // Load my patterns when page changes
  useEffect(() => {
    loadMyPatterns();
  }, [loadMyPatterns]);

  // Reset public patterns when search term changes
  useEffect(() => {
    setPublicPatterns([]);
    setPublicPage(1);
    setHasMorePublic(true);
  }, [searchTerm]);

  // Load public patterns when page or search changes
  useEffect(() => {
    loadPublicPatterns();
  }, [loadPublicPatterns]);

  // Load public patterns on initial mount
  useEffect(() => {
    const loadInitialPublic = async () => {
      try {
        const params = new URLSearchParams();
        params.append('page', '1');
        params.append('page_size', '12');
        
        const response = await axios.get(`${API}/patterns/public?${params.toString()}`);
        const newItems = response.data.items || response.data;
        setPublicPatterns(newItems);
        setPublicTotal(response.data.total || newItems.length);
        setHasMorePublic(1 < (response.data.total_pages || 1));
      } catch (error) {
        logger.error('Failed to load public patterns:', error);
      }
    };
    loadInitialPublic();
  }, []);

  const handleLike = async (patternId) => {
    try {
      await axios.post(`${API}/patterns/${patternId}/like`, {}, { withCredentials: true });
      toast.success(t('patterns.liked', 'Pattern liked!'));
      // Reload public patterns
      setPublicPatterns([]);
      setPublicPage(1);
      setHasMorePublic(true);
    } catch (error) {
      logger.error('Failed to like pattern:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            <p className="mt-4 text-muted-foreground">{t('common.loading', 'Loading...')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <PatternsContent 
          myPatterns={myPatterns}
          publicPatterns={publicPatterns}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          navigate={navigate}
          handleLike={handleLike}
          hasMorePublic={hasMorePublic}
          publicTotal={publicTotal}
          myTotal={myTotal}
          hasMoreMy={hasMoreMy}
          loadingMoreMy={loadingMoreMy}
          loadingMorePublic={loadingMorePublic}
          lastMyPatternRef={lastMyPatternRef}
          lastPublicPatternRef={lastPublicPatternRef}
          t={t}
        />
      </main>
    </div>
  );
}

function PatternsContent({ myPatterns, publicPatterns, searchTerm, setSearchTerm, navigate, handleLike, hasMorePublic, publicTotal, myTotal, hasMoreMy, loadingMoreMy, loadingMorePublic, lastMyPatternRef, lastPublicPatternRef, t }) {
  return (
    <>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-4xl md:text-5xl font-heading font-extrabold tracking-tight mb-2">
            {t('patterns.title', 'Patterns')}
          </h1>
          <p className="text-lg text-muted-foreground font-body">
            {t('patterns.subtitle', 'Save and share knitting & crochet patterns')}
          </p>
        </div>
        <Button
          data-testid="add-pattern-button"
          onClick={() => navigate('/patterns/add')}
          className="rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-0.5 transition-all duration-300 font-bold"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('patterns.addPattern', 'Add Pattern')}
        </Button>
      </div>

      <Tabs defaultValue="my-patterns" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2 mb-8">
          <TabsTrigger value="my-patterns">{t('patterns.myPatterns', 'My Patterns')}</TabsTrigger>
          <TabsTrigger value="community">{t('patterns.community', 'Community')}</TabsTrigger>
        </TabsList>

        <TabsContent value="my-patterns">
          {myPatterns.length === 0 ? (
            <div className="text-center py-20 rounded-3xl bg-white border border-stone-100">
              <BookOpen className="h-24 w-24 text-muted-foreground/30 mx-auto mb-6" />
              <h3 className="text-2xl font-heading font-semibold mb-2">{t('patterns.noPatterns', 'No patterns yet')}</h3>
              <p className="text-muted-foreground font-body mb-6">
                {t('patterns.noPatternsMessage', 'Create your first pattern and share it with the community')}
              </p>
              <Button
                onClick={() => navigate('/patterns/add')}
                className="rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-0.5 transition-all duration-300 font-bold"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('patterns.createPattern', 'Create Pattern')}
              </Button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {myPatterns.map((pattern, index) => (
                  <div 
                    key={pattern.pattern_id}
                    ref={index === myPatterns.length - 1 ? lastMyPatternRef : null}
                  >
                    <PatternCard pattern={pattern} isMine={true} onNavigate={navigate} onLike={handleLike} t={t} />
                  </div>
                ))}
              </div>
              {loadingMoreMy && <LoadingIndicator />}
              {!hasMoreMy && myPatterns.length > 0 && (
                <p className="text-center text-muted-foreground py-8">
                  {t('patterns.seenAllMy', "You've seen all {{count}} patterns", { count: myTotal })}
                </p>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="community">
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder={t('patterns.searchPlaceholder', 'Search patterns...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 rounded-xl"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {publicPatterns.map((pattern, index) => (
              <div
                key={pattern.pattern_id}
                ref={index === publicPatterns.length - 1 ? lastPublicPatternRef : null}
              >
                <PatternCard pattern={pattern} isMine={false} onNavigate={navigate} onLike={handleLike} t={t} />
              </div>
            ))}
          </div>
          {loadingMorePublic && <LoadingIndicator />}
          {!hasMorePublic && publicPatterns.length > 0 && (
            <p className="text-center text-muted-foreground py-8">
              {t('patterns.seenAllPublic', "You've seen all {{count}} community patterns", { count: publicTotal })}
            </p>
          )}
        </TabsContent>
      </Tabs>
    </>
  );
}
